import {
  AddRounded, ArrowDownwardRounded, ArrowUpwardRounded, CheckRounded, CloseRounded,
} from '@mui/icons-material';
import {
  Box, Chip, IconButton, InputBase, MenuItem, Popover, Select, Stack,
} from '@mui/material';
import {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import ReactGA from 'react-ga4';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { useComposition } from 'src/hooks/useComposition';

import { isActiveFilterRow } from './isActiveFilterRow';
import { LegacyPresetImportDialog } from './LegacyPresetImportDialog';
import {
  FilterPreset, normalizeFilterRows, normalizePresetRowsForSave, resolvePreset,
} from './presets';
import {
  emptyValueFor, expectedValueKind, FilterColumnDef, FilterOperator, FilterRow, FilterValue, OPERATORS_BY_TYPE,
} from './types';

export type AdvancedFilterPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columns: FilterColumnDef[];
  rows: FilterRow[];
  onRowsChange: (rows: FilterRow[]) => void;
  companyId: string;
  isLicenseReady: boolean;
  isMastersReady: boolean;
  /**
   * マスタ（トラック / ドライバー）の取得が失敗したか。default false。
   * isMastersReady 単独では「ロード中」と「取得失敗」を区別できず、失敗時に
   * 「候補の検証を省略して適用する」分岐が書けないため独立した prop にする。
   */
  isMastersError?: boolean;
  onNotify: (message: string) => void;
  /**
   * 並び替え。サンプル（route-planner）は 並び替え / フィルター / 保存済みフィルターを
   * 1 つの Popover にまとめているため、この Popover が並び替え UI も受け持つ。
   * state はリスト側が持ち、値と setter だけを渡す。
   */
  sortField: SortField;
  sortOrder: SortOrder;
  onSortFieldChange: (value: SortField) => void;
  onSortOrderChange: (value: SortOrder) => void;
  /**
   * 「同一地点で割当済」の先頭表示 (log_web_issues#332)。並び替えと同じく state は
   * リスト側が持ち、値と setter だけを渡す（この Popover は localStorage を触らない）。
   */
  sameLocationMode: SameLocationMode;
  onSameLocationModeChange: (value: SameLocationMode) => void;
  /** タグが付く案件数（絞り込み前の全件）。0 でも節は出す（log_web_issues#339） */
  sameLocationCount: number;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
  /**
   * 保存済みプリセット。保存先はサーバ（会社共有）で、取得も保存も呼び出し側が持つ。
   * この Popover は localStorage を触らない。
   */
  presets: FilterPreset[];
  /** rows は保存用に正規化済み（無効行を落とし OR 境界を整えたもの）。同名は上書き */
  onSavePreset: (name: string, rows: FilterRow[]) => void;
  onDeletePreset: (name: string) => void;
  /**
   * 旧画面 (/plans) の条件の取り込み保存。onSavePreset と分けるのは、取り込みは
   * 「保存できてから閉じる」ので成否の通知が要るため（onSavePreset の呼び出し側は
   * 通知を受け取らない）。optional にしないのは、渡し忘れると取り込みボタンだけが
   * 黙って消えて気づけないため。
   */
  onSaveImportedPreset: (
    name: string,
    rows: FilterRow[],
    callbacks: { onSuccess: () => void; onError: () => void },
  ) => void;
};

export type SortField = 'quantity' | 'weight' | 'volume';
export type SortOrder = 'asc' | 'desc';
export type SameLocationMode = 'off' | 'priority';

export function createFilterRow(columns: FilterColumnDef[], combinator: FilterRow['combinator'] = 'and'): FilterRow {
  const column = columns[0];
  const operator = OPERATORS_BY_TYPE[column.type][0].value;
  // 連番カウンタは module scope のためページ再読み込みで 0 に戻り、localStorage に
  // 永続する保存済みプリセットの行 id（例: f-1）と衝突し得た。crypto.randomUUID() で
  // 衝突しない id を生成する（同様の用法は ConvenienceDispatchAddVehicleDialog.tsx 参照）。
  return { id: crypto.randomUUID(), combinator, column: column.key, operator, value: emptyValueFor(column.type, operator) };
}

// 並び替えボタンの見た目。表示単位トグルとも共有するためリスト側からも import する。
export const sortBtnSx = (active: boolean) => ({
  borderRadius: 1,
  px: 1.25,
  py: 0.75,
  fontSize: 12,
  bgcolor: active ? 'hsl(var(--primary) / 0.1)' : 'transparent',
  fontWeight: active ? 600 : 400,
  color: active ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
  border: 'none',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  '&:hover': active ? {} : { bgcolor: 'hsl(var(--secondary))' },
});

const selectSx = { height: 28, fontSize: 11, '& .MuiSelect-select': { py: 0.5 } };

/**
 * 先頭行の Where ラベルと 2 行目以降の AND/OR セレクトで共有する左カラム幅。
 *
 * 内訳: OutlinedInput size=small の左 padding 14px + Select の icon 用 paddingRight 32px
 * + 「AND」の実描画幅（Noto Sans JP 11px で 25.11px を Chrome で実測）= 71.11px。
 * 余白を足して 80。以前の 64px では 7px 足りず「A…」に潰れていた。
 * Where(38.47px) も同じ幅にして、行頭の縦位置を揃える。
 */
const COMBINATOR_WIDTH = 80;
const menuItemSx = { fontSize: 12 };
const valueInputSx = { height: 28, fontSize: 11, px: 1, border: 1, borderColor: 'divider', borderRadius: 1 };
const footerButtonSx = {
  display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 1, px: 1, py: 0.75, fontSize: 12,
  color: 'hsl(var(--muted-foreground))', bgcolor: 'transparent', border: 'none', cursor: 'pointer',
  '&:hover': { bgcolor: 'hsl(var(--secondary))', color: 'hsl(var(--foreground))' },
};

/**
 * IME 対応のテキスト入力。変換中（未確定）は表示だけ追随させ、`onCommit` を呼ばない。
 * 確定前に絞り込むと、変換候補を選ぶ前にリストがちらつく。
 *
 * Why not 入力自体を止める: 文字が打てなくなる。止めるのは「反映」だけ。
 * Why not isComposing だけを見る: IME を通さない入力（英数字・貼り付け）は
 * compositionStart が来ないので、そちらが永久に反映されなくなる。
 */
const ImeTextInput: FC<{
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  sx?: object;
}> = ({ value, onCommit, placeholder, sx }) => {
  const [draft, setDraft] = useState(value);
  const { isComposing, onCompositionStart, onCompositionEnd } = useComposition();

  // 外から値が変わったら（すべてクリア・プリセット適用など）追随する
  useEffect(() => { setDraft(value); }, [value]);

  return (
    <InputBase
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        if (!isComposing) onCommit(e.target.value);
      }}
      onCompositionStart={onCompositionStart}
      onCompositionEnd={(e) => {
        onCompositionEnd();
        onCommit((e.target as HTMLInputElement).value);
      }}
      placeholder={placeholder}
      sx={sx}
    />
  );
};

/** 値入力を列型 × 演算子で切り替える。expectedValueKind の結果だけを見て分岐すること（列だけで決め打ちしない）。 */
const ValueInput: FC<{
  column: FilterColumnDef;
  row: FilterRow;
  onChange: (value: FilterValue) => void;
}> = ({ column, row, onChange }) => {
  const kind = expectedValueKind(column.type, row.operator);

  if (kind === 'none') return null;

  if (kind === 'text' && row.value.kind === 'text') {
    return (
      <ImeTextInput
        value={row.value.value}
        onCommit={(value) => onChange({ kind: 'text', value })}
        placeholder="値を入力…"
        sx={{ ...valueInputSx, flex: 1 }}
      />
    );
  }

  if (kind === 'number' && row.value.kind === 'number') {
    return (
      <InputBase
        type="number"
        value={row.value.value ?? ''}
        onChange={(e) => onChange({ kind: 'number', value: e.target.value === '' ? null : Number(e.target.value) })}
        placeholder="値を入力…"
        sx={{ ...valueInputSx, width: 90 }}
      />
    );
  }

  if (kind === 'numberRange' && row.value.kind === 'numberRange') {
    const { min, max } = row.value;
    return (
      <Stack direction="row" alignItems="center" gap={0.5}>
        <InputBase
          type="number"
          value={min ?? ''}
          onChange={(e) => onChange({ kind: 'numberRange', min: e.target.value === '' ? null : Number(e.target.value), max })}
          placeholder="最小"
          sx={{ ...valueInputSx, width: 70 }}
        />
        <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>〜</Box>
        <InputBase
          type="number"
          value={max ?? ''}
          onChange={(e) => onChange({ kind: 'numberRange', min, max: e.target.value === '' ? null : Number(e.target.value) })}
          placeholder="最大"
          sx={{ ...valueInputSx, width: 70 }}
        />
        <Box component="span" sx={{ fontSize: 10, color: 'text.secondary' }}>最小と最大の両方を入力してください</Box>
      </Stack>
    );
  }

  if (kind === 'time' && row.value.kind === 'time') {
    return (
      <InputBase
        type="time"
        value={row.value.value}
        onChange={(e) => onChange({ kind: 'time', value: e.target.value })}
        sx={{ ...valueInputSx, width: 90 }}
      />
    );
  }

  if (kind === 'enum' && row.value.kind === 'enum' && column.type === 'enumSelect') {
    const labelByValue = new Map(column.options.map((o) => [o.value, o.label]));
    return (
      <Select<string[]>
        size="small"
        multiple
        value={row.value.values}
        onChange={(e) => onChange({ kind: 'enum', values: e.target.value as string[] })}
        renderValue={(values) => values.map((v) => labelByValue.get(v) ?? v).join(', ')}
        sx={{ ...selectSx, width: 140 }}
      >
        {column.options.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>
        ))}
      </Select>
    );
  }

  if (kind === 'id' && row.value.kind === 'id' && column.type === 'idSelect') {
    const labelByValue = new Map(column.options.map((o) => [o.value, o.label]));
    return (
      <Select<number[]>
        size="small"
        multiple
        value={row.value.values}
        onChange={(e) => onChange({ kind: 'id', values: e.target.value as number[] })}
        renderValue={(values) => values.map((v) => labelByValue.get(v) ?? v).join(', ')}
        sx={{ ...selectSx, width: 140 }}
      >
        {column.options.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>
        ))}
      </Select>
    );
  }

  if (kind === 'boolean' && row.value.kind === 'boolean') {
    const stringValue: string = row.value.value === null ? '' : row.value.value ? 'true' : 'false';
    return (
      <Select
        size="small"
        value={stringValue}
        onChange={(e) => {
          const next = e.target.value;
          onChange({ kind: 'boolean', value: next === '' ? null : next === 'true' });
        }}
        sx={{ ...selectSx, width: 90 }}
      >
        <MenuItem value="" sx={menuItemSx}>未選択</MenuItem>
        <MenuItem value="true" sx={menuItemSx}>可</MenuItem>
        <MenuItem value="false" sx={menuItemSx}>不可</MenuItem>
      </Select>
    );
  }

  return null;
};

export const AdvancedFilterPopover: FC<AdvancedFilterPopoverProps> = ({
  open, anchorEl, onClose, columns, rows, onRowsChange, companyId, isLicenseReady, isMastersReady, onNotify,
  isMastersError = false, sortField, sortOrder, onSortFieldChange, onSortOrderChange,
  sameLocationMode, onSameLocationModeChange, sameLocationCount, screenName,
  presets, onSavePreset, onDeletePreset, onSaveImportedPreset,
}) => {
  const columnsByKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);

  const sendGA = useCallback((buttonName: string, label?: string) => {
    ReactGA.event('click', { screen_name: screenName, button_name: buttonName, ...(label ? { label } : {}) });
  }, [screenName]);

  // pending は会社 ID と対で持つ。会社が変わると列も候補も別物になるため、
  // 旧会社の pending を新会社の columns で解決してはならない。
  const [pending, setPending] = useState<{ companyId: string; preset: FilterPreset } | null>(null);

  /** 手動で条件を編集したら待機中の適用は取り消す（あとから上書きされると編集が消えるため）。 */
  const cancelPending = useCallback(() => setPending(null), []);

  // 会社が変わったら旧会社の pending は破棄する
  useEffect(() => {
    setPending((p) => (p && p.companyId !== companyId ? null : p));
  }, [companyId]);

  /**
   * 第2引数は「候補の検証を省略した」ことの通知フラグ。resolvePreset へ渡す isMastersReady は
   * 3 状態のどれでも変えない。presets.ts:187 は `if (!isMastersReady) return row;` で
   * false が省略側なので、エラー時に true を渡すと空の候補で照合が走り
   * values.length === 0 の分岐（:189-200）で保存済みトラック / ドライバー ID が消える。
   */
  const applyResolved = useCallback((preset: FilterPreset, masterValidationSkipped: boolean) => {
    const { rows: resolved, droppedCount } = resolvePreset(preset, columns, { isLicenseReady, isMastersReady });
    onRowsChange(resolved);
    if (masterValidationSkipped) onNotify('トラック・ドライバーの取得に失敗したため、候補の検証を省略しました');
    else if (droppedCount > 0) onNotify('一部の条件は現在の設定に無いため除外しました');
  }, [columns, isLicenseReady, isMastersReady, onNotify, onRowsChange]);

  useEffect(() => {
    if (!pending || pending.companyId !== companyId) return;
    if (!isLicenseReady) return;
    if (isMastersReady) { applyResolved(pending.preset, false); setPending(null); return; }
    // マスタ取得が失敗したなら、候補が揃うことは無いので待たずに適用する
    // （候補が取れないことを理由に保存済み ID を消すと設定が失われる）。
    if (isMastersError) { applyResolved(pending.preset, true); setPending(null); }
  }, [pending, companyId, isLicenseReady, isMastersReady, isMastersError, applyResolved]);

  const handlePresetClick = useCallback((preset: FilterPreset) => {
    sendGA('プリセット 適用', preset.name);
    // ready → 通常 / error → 検証省略で適用 / それ以外（ロード中）→ ready・error になってから解決
    if (isLicenseReady && isMastersReady) { applyResolved(preset, false); return; }
    if (isLicenseReady && isMastersError) { applyResolved(preset, true); return; }
    setPending({ companyId, preset });
  }, [applyResolved, companyId, isLicenseReady, isMastersReady, isMastersError, sendGA]);

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const handleConfirmDelete = useCallback(() => {
    if (deleteTarget) {
      sendGA('プリセット 削除', deleteTarget);
      onDeletePreset(deleteTarget);
    }
    setDeleteTarget(null);
  }, [deleteTarget, onDeletePreset, sendGA]);

  const [presetNameInput, setPresetNameInput] = useState<string | null>(null);
  // 変換確定の Enter と、保存する意思の Enter を区別するために使う
  const presetNameComposition = useComposition();
  const commitSavePreset = useCallback(() => {
    const name = presetNameInput?.trim();
    if (!name) return;
    // 正規化は無効行を全部落とすので、有効行が 0 件だと rows: [] のプリセットができ、
    // 適用すると条件が全消えする。保存前に弾く。
    const activeCount = rows.filter((row) => {
      const column = columnsByKey.get(row.column);
      return column && isActiveFilterRow(row, column);
    }).length;
    if (activeCount === 0) {
      onNotify('条件が入力されていないため保存できません');
      return;
    }
    onSavePreset(name, normalizePresetRowsForSave(rows, columnsByKey));
    sendGA('プリセット 保存', name);
    setPresetNameInput(null);
  }, [presetNameInput, rows, columnsByKey, onNotify, onSavePreset, sendGA]);

  const [isImportOpen, setIsImportOpen] = useState(false);
  const presetNames = useMemo(() => presets.map((p) => p.name), [presets]);

  const updateRow = useCallback((id: string, patch: Partial<FilterRow>) => {
    cancelPending();
    if (patch.column) sendGA('フィルター 列', patch.column);
    if (patch.operator) sendGA('フィルター 演算子', patch.operator);
    onRowsChange(rows.map((row) => {
      if (row.id !== id) return row;
      const nextColumnKey = patch.column ?? row.column;
      const column = columns.find((c) => c.key === nextColumnKey);
      if (!column) return row;

      const allowed = OPERATORS_BY_TYPE[column.type].map((o) => o.value);
      const nextOperator: FilterOperator = patch.operator && allowed.includes(patch.operator)
        ? patch.operator
        : (allowed.includes(row.operator) ? row.operator : allowed[0]);

      const columnChanged = nextColumnKey !== row.column;
      const operatorChanged = nextOperator !== row.operator;
      const value = patch.value ?? (columnChanged || operatorChanged ? emptyValueFor(column.type, nextOperator) : row.value);

      return { ...row, column: nextColumnKey, operator: nextOperator, value };
    }));
  }, [cancelPending, columns, onRowsChange, rows, sendGA]);

  // combinator (AND/OR) は値の kind に影響しないため、updateRow の列/演算子リビルド規則とは別に単純に差し替える。
  const updateCombinator = useCallback((id: string, combinator: 'and' | 'or') => {
    cancelPending();
    onRowsChange(rows.map((row) => (row.id === id ? { ...row, combinator } : row)));
  }, [cancelPending, onRowsChange, rows]);

  const addRow = useCallback(() => {
    cancelPending();
    sendGA('フィルター 追加');
    // 空の状態からの追加は先頭行になるため where 固定にする（先頭行だけ AND/OR を持たない仕様）。
    // 2 行目以降は createFilterRow の既定どおり and にする（ここを where にすると
    // activeGroups の OR グルーピングが崩れる）。
    onRowsChange([...rows, createFilterRow(columns, rows.length === 0 ? 'where' : 'and')]);
  }, [cancelPending, columns, onRowsChange, rows, sendGA]);

  const deleteRow = useCallback((id: string) => {
    cancelPending();
    sendGA('フィルター行 削除');
    // 単純な filter だと OR 境界が壊れる（例: A(where)/B(or)/C(and) から B を削除すると
    // A(where)/C(and) = A AND C になり、本来の A OR C から意味が変わる）。
    // normalizeFilterRows は連続 AND のグループを保ったまま落とし、生き残ったグループの
    // 先頭を where/or へ正規化するため、プリセット適用・保存と同じ規則で削除できる。
    onRowsChange(normalizeFilterRows(rows, (row) => (row.id === id ? null : row)));
  }, [cancelPending, onRowsChange, rows, sendGA]);

  const clearAll = useCallback(() => {
    cancelPending();
    sendGA('フィルター 全クリア');
    onRowsChange([{ ...createFilterRow(columns), combinator: 'where' }]);
  }, [cancelPending, columns, onRowsChange, sendGA]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 520, maxHeight: 520, overflowY: 'auto', p: 1.5 } } }}
    >
      <Box sx={{ '& > * + *': { mt: 1.5 } }}>
        {/* 並び替え。サンプル準拠で 並び替え → フィルター → 保存済みフィルター の順に並べる。
            単独の Popover に分けると幅が足りずボタン文字が縦に折り返すため、この 520px の
            Popover に同居させる。 */}
        <Box sx={{ '& > * + *': { mt: 0.75 } }}>
          <Box component="p" sx={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--foreground))', m: 0 }}>並び替え</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {([['quantity', '数量'], ['weight', '重量'], ['volume', '体積']] as const).map(([value, label]) => (
              <Box
                key={value}
                component="button"
                type="button"
                onClick={() => {
                  ReactGA.event('select', { screen_name: screenName, button_name: '未割当 ソート列', label: value });
                  onSortFieldChange(value);
                }}
                sx={sortBtnSx(sortField === value)}
              >
                {label}
              </Box>
            ))}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {([['asc', '昇順'], ['desc', '降順']] as const).map(([value, label]) => (
                <Box
                  key={value}
                  component="button"
                  type="button"
                  onClick={() => {
                    ReactGA.event('select', { screen_name: screenName, button_name: '未割当 ソート順', label: value === 'asc' ? 'ASC' : 'DESC' });
                    onSortOrderChange(value);
                  }}
                  sx={{ ...sortBtnSx(sortOrder === value), px: 1 }}
                >
                  {value === 'asc' ? <ArrowUpwardRounded sx={{ fontSize: 12 }} /> : <ArrowDownwardRounded sx={{ fontSize: 12 }} />}
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* 同一地点で割当済（log_web_issues#332）。サンプル準拠で 並び替え の下・フィルター の上。
            件数 0 でも節を常に出す（log_web_issues#339）。サンプル（route-planner）は 0 件で
            節ごと隠すが、隠すと保存済みの 先頭表示 を 0 件の日に確認も変更もできない。 */}
        <Box sx={{ borderTop: '1px solid hsl(var(--border))', pt: 1, '& > * + *': { mt: 0.75 } }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Box component="p" sx={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--foreground))', m: 0 }}>同一地点で割当済</Box>
            <Box component="span" sx={{ fontSize: 10, color: 'hsl(var(--muted-foreground))' }}>{sameLocationCount} 件</Box>
          </Box>
          <Box component="span" sx={{ display: 'block', fontSize: 10, color: 'hsl(var(--muted-foreground))', lineHeight: 1.4 }}>
            同じ地点の案件がすでに車両へ割当済みの未割当案件
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {([['off', '通常表示'], ['priority', '先頭表示']] as const).map(([value, label]) => (
              <Box
                key={value}
                component="button"
                type="button"
                onClick={() => {
                  ReactGA.event('select', { screen_name: screenName, button_name: '未割当 同一地点表示', label: value });
                  onSameLocationModeChange(value);
                }}
                sx={{ ...sortBtnSx(sameLocationMode === value), px: 1 }}
                // 隣の 並び替え / 表示単位 のトグルには付いていないが、既存への後付けは
                // #332 のスコープ外にする（テストのセレクタを触る範囲が広がる）
                aria-pressed={sameLocationMode === value}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Filter rows */}
        <Box sx={{ borderTop: '1px solid hsl(var(--border))', pt: 1, '& > * + *': { mt: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box component="p" sx={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--foreground))', m: 0 }}>フィルター</Box>
            <Box component="button" type="button" onClick={clearAll} sx={{ fontSize: '10px', color: 'hsl(var(--muted-foreground))', bgcolor: 'transparent', border: 'none', cursor: 'pointer', '&:hover': { color: 'hsl(var(--foreground))' } }}>
              すべてクリア
            </Box>
          </Box>

          {rows.map((row) => {
            const column = columnsByKey.get(row.column) ?? columns[0];
            const isBoolean = column.type === 'boolean';

            return (
              <Stack key={row.id} direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                {row.combinator === 'where' ? (
                  <Box sx={{ width: COMBINATOR_WIDTH, fontSize: 11, fontWeight: 600, color: 'hsl(var(--foreground))' }}>Where</Box>
                ) : (
                  <Select
                    size="small"
                    value={row.combinator}
                    onChange={(e) => updateCombinator(row.id, e.target.value)}
                    sx={{ ...selectSx, width: COMBINATOR_WIDTH }}
                  >
                    <MenuItem value="and" sx={menuItemSx}>AND</MenuItem>
                    <MenuItem value="or" sx={menuItemSx}>OR</MenuItem>
                  </Select>
                )}

                <Select
                  size="small"
                  value={row.column}
                  onChange={(e) => updateRow(row.id, { column: e.target.value })}
                  sx={{ ...selectSx, width: 118 }}
                >
                  {columns.map((c) => (
                    <MenuItem key={c.key} value={c.key} sx={menuItemSx}>{c.label}</MenuItem>
                  ))}
                </Select>

                {!isBoolean && (
                  <Select
                    size="small"
                    value={row.operator}
                    aria-label="演算子"
                    onChange={(e) => updateRow(row.id, { operator: e.target.value })}
                    sx={{ ...selectSx, width: 118 }}
                  >
                    {OPERATORS_BY_TYPE[column.type].map((o) => (
                      <MenuItem key={o.value} value={o.value} sx={menuItemSx}>{o.label}</MenuItem>
                    ))}
                  </Select>
                )}

                <ValueInput column={column} row={row} onChange={(value) => updateRow(row.id, { value })} />

                {row.combinator !== 'where' && (
                  <IconButton
                    size="small"
                    aria-label="この条件を削除"
                    onClick={() => deleteRow(row.id)}
                    sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                  >
                    <CloseRounded sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Stack>
            );
          })}

          <Box component="button" type="button" onClick={addRow} sx={footerButtonSx}>
            <AddRounded sx={{ fontSize: 12 }} />
            条件を追加
          </Box>

        {/* Presets */}
        <Box sx={{ borderTop: '1px solid hsl(var(--border))', pt: 1, '& > * + *': { mt: 0.75 } }}>
          {/* 見出しと「現在の条件を保存」は同じ行。下に置くと保存済みの一覧と
              操作が入り混じって見える（サンプル UnassignedPanel.tsx:1673-1739） */}
          <Box
            data-testid="preset-header"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Box component="p" sx={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--foreground))', m: 0 }}>
              {pending
                ? '保存済みフィルター（読み込み中）'
                : `保存済みフィルター${presets.length > 0 ? ` (${presets.length})` : ''}`}
            </Box>
            {presetNameInput === null && (
              <Box
                component="button"
                type="button"
                onClick={() => setPresetNameInput('')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  fontSize: 10,
                  color: 'hsl(var(--primary))',
                  bgcolor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <AddRounded sx={{ fontSize: 12 }} />
                現在の条件を保存
              </Box>
            )}
          </Box>
          {presets.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {presets.map((preset) => (
                <Chip
                  key={preset.name}
                  label={preset.name}
                  size="small"
                  disabled={!!pending}
                  onClick={() => handlePresetClick(preset)}
                  onDelete={() => setDeleteTarget(preset.name)}
                  sx={{ fontSize: 11, height: 22 }}
                />
              ))}
            </Stack>
          ) : (
            // 空欄のままだと、ここが何をする場所なのか分からない
            <Box sx={{ fontSize: 10, fontStyle: 'italic', color: 'hsl(var(--muted-foreground))' }}>
              よく使う条件を保存すると、ここからワンクリックで呼び出せます
            </Box>
          )}
          {/* 取り込みはプリセット一覧の下。旧画面の条件を新形式のプリセットとして保存する操作なので、
              保存済みフィルターのブロックに置く（設計書「UI」節） */}
          <Box
            component="button"
            type="button"
            onClick={() => { sendGA('旧画面の条件を取り込む'); setIsImportOpen(true); }}
            sx={footerButtonSx}
          >
            旧画面の条件を取り込む
          </Box>
          {presetNameInput !== null && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <InputBase
                inputProps={{ 'aria-label': 'プリセット名' }}
                value={presetNameInput}
                onChange={(e) => setPresetNameInput(e.target.value)}
                onCompositionStart={presetNameComposition.onCompositionStart}
                onCompositionEnd={presetNameComposition.onCompositionEnd}
                onKeyDown={(e) => {
                  // 変換確定の Enter で保存すると、意図しない名前でプリセットが作られる
                  if (presetNameComposition.isComposing) return;
                  if (e.key === 'Enter') commitSavePreset();
                  if (e.key === 'Escape') setPresetNameInput(null);
                }}
                placeholder="プリセット名"
                sx={{ ...valueInputSx, flex: 1 }}
              />
              {/* 既定の黒だと「押していい操作」に見えないので、保存は緑・取消は淡色にする */}
              <IconButton
                size="small"
                aria-label="プリセット名を保存"
                onClick={commitSavePreset}
                sx={{ p: 0.25, color: 'success.main' }}
              >
                <CheckRounded sx={{ fontSize: 14 }} />
              </IconButton>
              <IconButton
                size="small"
                aria-label="プリセット名の入力を取消"
                onClick={() => setPresetNameInput(null)}
                sx={{ p: 0.25, color: 'text.secondary' }}
              >
                <CloseRounded sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          )}
        </Box>
        </Box>
      </Box>

      <LegacyPresetImportDialog
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        columns={columns}
        companyId={companyId}
        isLicenseReady={isLicenseReady}
        isMastersReady={isMastersReady}
        isMastersError={isMastersError}
        existingPresetNames={presetNames}
        onSave={onSaveImportedPreset}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        message={`保存済みフィルター「${deleteTarget ?? ''}」を削除しますか？この操作は元に戻せません。`}
        handleOk={handleConfirmDelete}
        handleCancel={() => setDeleteTarget(null)}
      />
    </Popover>
  );
};
